import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { RestService } from '../../shared/rest.service';

/**
 * Resuelve la URL de imagen de un destino consultando el backend cuando
 * el summary del itinerario no trae `inmg_zones` (o las trae vacías).
 *
 * Cachea la respuesta por `destination_id` para evitar llamadas repetidas
 * cuando el mismo destino aparece varias veces en la UI.
 */
@Injectable()
export class DestinationImageService {
  private cache = new Map<string | number, Observable<string | null>>();

  constructor(private restService: RestService) {}

  /**
   * Devuelve un Observable que emite UNA URL de imagen para el destino,
   * o `null` si el backend no devuelve nada / falla.
   *
   * Acepta cualquier combinación de ids numéricos (destination_id, city_id, country_id)
   * y/o códigos IATA (city_iata_code, country_iata_code). Envía al backend solo los
   * que vengan con valor; el backend decide con qué resolver.
   *
   * Misma clave → mismo Observable (cacheado con `shareReplay`).
   */
  getImageByDestinationId(
    destinationId: string | number | null | undefined,
    cityId?: string | number | null,
    countryId?: string | number | null,
    cityIataCode?: string | null,
    countryIataCode?: string | null,
  ): Observable<string | null> {
    const hasAnyId =
      (destinationId !== null && destinationId !== undefined && destinationId !== '') ||
      (cityId !== null && cityId !== undefined && cityId !== '') ||
      (countryId !== null && countryId !== undefined && countryId !== '');
    const hasAnyIata = !!cityIataCode || !!countryIataCode;

    if (!hasAnyId && !hasAnyIata) {
      return of(null);
    }

    const cacheKey =
      `${destinationId ?? ''}|${cityId ?? ''}|${countryId ?? ''}` +
      `|${cityIataCode ?? ''}|${countryIataCode ?? ''}`;
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const params: Record<string, string | number> = {};
    if (destinationId !== null && destinationId !== undefined && destinationId !== '') {
      params.destination_id = destinationId;
    }

    const request$ = this.restService
      .doGet('app', 'itineraries/destination/images', { params })
      .pipe(
        map((response: any) => this.extractImageUrl(response)),
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.cache.set(cacheKey, request$);
    return request$;
  }

  /**
   * Extrae la URL de la respuesta de Octopus.
   * Ajustar según el shape real — ver TODO arriba.
   */
  private extractImageUrl(response: any): string | null {
    if (!response) {
      return null;
    }

    // Shape real: { success, results: { destinations: [{ images: [{ url }] }] } }
    // Se recorre cada destination y se devuelve la primera URL encontrada.
    const destinations = response?.results?.destinations;
    if (Array.isArray(destinations)) {
      for (const dest of destinations) {
        const images = dest?.images;
        if (Array.isArray(images) && images.length > 0) {
          const first = images[0];
          if (typeof first?.url === 'string' && first.url) {
            return first.url;
          }
          if (typeof first?.path === 'string' && first.path) {
            return first.path;
          }
        }
      }
    }

    // Fallbacks para shapes anteriores (no romper si el backend cambia)
    if (Array.isArray(response?.data?.images) && response.data.images.length > 0) {
      const first = response.data.images[0];
      return typeof first === 'string' ? first : (first?.url ?? null);
    }

    if (typeof response?.data?.url === 'string' && response.data.url) {
      return response.data.url;
    }

    if (Array.isArray(response?.images) && response.images.length > 0) {
      const first = response.images[0];
      return typeof first === 'string' ? first : (first?.url ?? null);
    }

    return null;
  }
}
